import React, { useId } from 'react';
import { SSelect } from '@chargebee/sting-react';

type Value = string | number | boolean | undefined;
type Option<T extends Value> = {
  value: T;
  label?: React.ReactNode;
  isDisabled?: boolean;
};
type Group<T extends Value> = { label: React.ReactNode; options: Option<T>[] };

type Props<T extends Value> = {
  options: Array<Option<T> | Group<T>>;
  value?: Option<T>;
  onChange: (option: Option<T>) => void;
  disabled?: boolean;
  searchable?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  renderOption?: (option: Option<T>) => React.ReactNode;
};
const keyFor = (value: Value) => `${typeof value}:${String(value)}`;

/** Maps Page placement values (including None and booleans) to Sting selections. */
export const PageEditorSelect = <T extends Value>({
  options,
  value,
  onChange,
  disabled,
  searchable = true,
  label,
  placeholder,
  renderOption,
}: Props<T>) => {
  const id = useId();
  const flat = options.flatMap((option) =>
    'options' in option ? option.options : [option]
  );
  const renderItem = (option: Option<T>) => (
    <SSelect.Item
      key={keyFor(option.value)}
      value={keyFor(option.value)}
      disabled={option.isDisabled}
      label={typeof option.label === 'string' ? option.label : undefined}
    >
      {renderOption ? renderOption(option) : option.label}
    </SSelect.Item>
  );
  return (
    <SSelect
      disabled={disabled}
      searchable={searchable}
      clearable={false}
      value={value ? keyFor(value.value) : ''}
      options={flat.map((option) => ({
        value: keyFor(option.value),
        label:
          typeof option.label === 'string'
            ? option.label
            : String(option.value ?? 'None'),
        disabled: option.isDisabled,
      }))}
      onValueChange={(selected) => {
        const option = flat.find((option) => keyFor(option.value) === selected);
        if (option) onChange(option);
      }}
    >
      {label && <SSelect.Label htmlFor={id}>{label}</SSelect.Label>}
      <SSelect.Trigger id={id}>
        <SSelect.Value placeholder={placeholder} displayText={value?.label} />
      </SSelect.Trigger>
      {/* Sting 2.6.6 compound selects otherwise sit below the modal portal. */}
      <SSelect.Content className="!tw-z-[10001]">
        {searchable && <SSelect.Input placeholder="Search options" />}
        <SSelect.List>
          <SSelect.Empty>No matching options</SSelect.Empty>
          {options.map((option) =>
            'options' in option ? (
              <SSelect.Group key={String(option.label)} heading={option.label}>
                {option.options.map(renderItem)}
              </SSelect.Group>
            ) : (
              renderItem(option)
            )
          )}
        </SSelect.List>
      </SSelect.Content>
    </SSelect>
  );
};
